import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { BPReading } from '../utils/api';

interface BPEntryFormProps {
  onSubmit: (reading: Omit<BPReading, 'id' | 'timestamp'>) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
}

const BPEntryForm: React.FC<BPEntryFormProps> = ({ 
  onSubmit, 
  isOpen, 
  onClose, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    systolic: '',
    diastolic: '',
    heart_rate: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    const systolic = parseInt(formData.systolic);
    const diastolic = parseInt(formData.diastolic);
    const heartRate = formData.heart_rate ? parseInt(formData.heart_rate) : null;

    // Systolic validation
    if (!formData.systolic) {
      newErrors.systolic = 'Systolic pressure is required';
    } else if (systolic < 60 || systolic > 300) {
      newErrors.systolic = 'Systolic pressure must be between 60-300 mmHg';
    }

    // Diastolic validation
    if (!formData.diastolic) {
      newErrors.diastolic = 'Diastolic pressure is required';
    } else if (diastolic < 40 || diastolic > 200) {
      newErrors.diastolic = 'Diastolic pressure must be between 40-200 mmHg';
    }

    // Cross validation
    if (systolic && diastolic && systolic <= diastolic + 10) {
      newErrors.systolic = 'Systolic should be significantly higher than diastolic';
    }

    // Heart rate validation
    if (heartRate && (heartRate < 30 || heartRate > 250)) {
      newErrors.heart_rate = 'Heart rate must be between 30-250 bpm';
    }

    // Notes validation
    if (formData.notes.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const reading: Omit<BPReading, 'id' | 'timestamp'> = {
        systolic: parseInt(formData.systolic),
        diastolic: parseInt(formData.diastolic),
        heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : undefined,
        notes: formData.notes.trim() || undefined
      };

      await onSubmit(reading);
      
      // Reset form
      setFormData({
        systolic: '',
        diastolic: '',
        heart_rate: '',
        notes: ''
      });
      setErrors({});
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error submitting BP reading:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Enter BP Reading</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Blood Pressure Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Systolic *
              </label>
              <input
                type="number"
                value={formData.systolic}
                onChange={(e) => handleInputChange('systolic', e.target.value)}
                className={`input-field ${errors.systolic ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="120"
                min="60"
                max="300"
                disabled={loading}
              />
              {errors.systolic && (
                <p className="text-red-500 text-xs mt-1">{errors.systolic}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">mmHg</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Diastolic *
              </label>
              <input
                type="number"
                value={formData.diastolic}
                onChange={(e) => handleInputChange('diastolic', e.target.value)}
                className={`input-field ${errors.diastolic ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="80"
                min="40"
                max="200"
                disabled={loading}
              />
              {errors.diastolic && (
                <p className="text-red-500 text-xs mt-1">{errors.diastolic}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">mmHg</p>
            </div>
          </div>

          {/* Heart Rate */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Heart Rate (optional)
            </label>
            <input
              type="number"
              value={formData.heart_rate}
              onChange={(e) => handleInputChange('heart_rate', e.target.value)}
              className={`input-field ${errors.heart_rate ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="72"
              min="30"
              max="250"
              disabled={loading}
            />
            {errors.heart_rate && (
              <p className="text-red-500 text-xs mt-1">{errors.heart_rate}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">beats per minute</p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className={`input-field ${errors.notes ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Any symptoms, context, or observations..."
              rows={3}
              maxLength={500}
              disabled={loading}
            />
            {errors.notes && (
              <p className="text-red-500 text-xs mt-1">{errors.notes}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              {formData.notes.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Reading
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BPEntryForm;